<template>
  <article class="feature-card" :class="[toneClass, { 'feature-card--compact': compact }]">
    <div class="feature-icon" aria-hidden="true">
      <v-icon class="feature-icon__glyph" size="32">{{ feature.icon }}</v-icon>
    </div>
    <h3 class="feature-title">{{ feature.title }}</h3>
    <p class="feature-description">{{ feature.description }}</p>
  </article>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import type { LandingFeature, LandingCatalogVisualTone } from '@/types/landingCatalog.types'

export default defineComponent({
  name: 'LandingFeatureCard',

  props: {
    feature: {
      type: Object as PropType<LandingFeature>,
      required: true,
    },
    tone: {
      type: String as PropType<LandingCatalogVisualTone | 'default'>,
      default: 'default',
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    toneClass(): string | null {
      if (this.tone === 'default') return null
      return `feature-card--${this.tone}`
    },
  },
})
</script>

<style scoped>
.feature-card {
  background: white;
  padding: 1.75rem 1.5rem 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  height: 100%;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    border-color 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  margin-bottom: 1.25rem;
  border-radius: 16px;
  background: rgba(29, 53, 87, 0.06);
}

.feature-icon__glyph {
  color: #1d3557 !important;
}

.feature-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1d3557;
  margin: 0 0 0.75rem;
  line-height: 1.3;
}

.feature-description {
  color: #6c757d;
  line-height: 1.6;
  margin: 0;
  font-size: 0.98rem;
}

.feature-card--warm {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(224, 106, 0, 0.14);
  box-shadow: 0 4px 24px rgba(224, 106, 0, 0.07);
}

.feature-card--warm:hover {
  border-color: rgba(224, 106, 0, 0.28);
  box-shadow: 0 14px 36px rgba(224, 106, 0, 0.14);
}

.feature-card--warm .feature-icon {
  background: rgba(224, 106, 0, 0.12);
}

.feature-card--warm .feature-icon__glyph {
  color: #c45a00 !important;
}

.feature-card--cool {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(10px);
}

.feature-card--cool:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 224, 138, 0.45);
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.22);
}

.feature-card--cool .feature-icon {
  background: rgba(255, 224, 138, 0.2);
}

.feature-card--cool .feature-icon__glyph {
  color: #ffe08a !important;
}

.feature-card--cool .feature-title {
  color: #f4fbff;
}

.feature-card--cool .feature-description {
  color: rgba(244, 251, 255, 0.84);
}

.feature-card--brand {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 159, 28, 0.22);
  box-shadow: 0 4px 24px rgba(29, 53, 87, 0.08);
}

.feature-card--brand:hover {
  border-color: rgba(255, 159, 28, 0.38);
  box-shadow: 0 12px 32px rgba(255, 159, 28, 0.14);
}

.feature-card--brand .feature-icon {
  background: rgba(255, 159, 28, 0.18);
}

.feature-card--brand .feature-icon__glyph {
  color: #1d3557 !important;
}

.feature-card--compact {
  display: grid;
  grid-template-columns: 46px 1fr;
  grid-template-rows: auto auto;
  column-gap: 14px;
  row-gap: 0.2rem;
  align-items: start;
  padding: 18px;
  border-radius: 16px;
  text-align: left;
  height: auto;
}

.feature-card--compact:hover {
  transform: none;
}

.feature-card--compact .feature-icon {
  grid-column: 1;
  grid-row: 1 / span 2;
  width: 46px;
  height: 46px;
  margin-bottom: 0;
  border-radius: 12px;
  align-self: start;
}

.feature-card--compact .feature-icon__glyph {
  font-size: 1.15rem !important;
}

.feature-card--compact .feature-title {
  grid-column: 2;
  grid-row: 1;
  align-self: center;
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.25;
}

.feature-card--compact .feature-description {
  grid-column: 2;
  grid-row: 2;
  font-size: 15px;
  line-height: 1.5;
}

@media (prefers-reduced-motion: reduce) {
  .feature-card,
  .feature-card:hover {
    transition: none;
    transform: none;
  }
}
</style>
